import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';

import { useGetCruiseLine } from '~/hooks/apis/cruises/useGetCruiseLines';
import { useGetInclusionFilters } from '~/hooks/apis/cruises/useGetInclusionFilters';
import { useCreateInclusionFilters } from '~/hooks/apis/cruises/useInclusionFilterMutations';

import DepartureFilterTab from './components/DepartureFilterTab';
import RateCodeFilterTab from './components/RateCodeFilterTab';

type TabId = 'rate' | 'departure';

interface Props {
  open: boolean;
  inclusionId: string;
  vendorId: string;
  onSave: () => void;
  onClose: () => void;
}

export default function InclusionsFilterBulkModal({ open, inclusionId, vendorId, onSave, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState<TabId>('rate');
  const [selectedRateIds, setSelectedRateIds] = useState<Array<string>>([]);
  const [selectedDepartureIds, setSelectedDepartureIds] = useState<Array<string>>([]);

  const { mutateAsync: createFilters, isPending } = useCreateInclusionFilters();

  const { data: cruiseLineData } = useGetCruiseLine(vendorId, open);

  const vendorCode = cruiseLineData?.code;
  const vendorName = cruiseLineData?.name;

  const { data: existingFiltersData } = useGetInclusionFilters(inclusionId, open);

  const existingDepartureIds = useMemo(
    () => existingFiltersData?.result?.filter((f) => f.filterType === 'DEPARTURE').map((f) => f.filterId) ?? [],
    [existingFiltersData],
  );

  const existingRateIds = useMemo(
    () => existingFiltersData?.result?.filter((f) => f.filterType === 'RATE').map((f) => f.filterId) ?? [],
    [existingFiltersData],
  );

  const handleTabChange = useCallback((_: React.SyntheticEvent, value: TabId) => {
    setActiveTab(value);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedRateIds([]);
    setSelectedDepartureIds([]);
    setActiveTab('rate');
    onClose();
  }, [onClose]);

  const newCount = useMemo(
    () =>
      activeTab === 'rate'
        ? selectedRateIds.filter((id) => !existingRateIds.includes(id)).length
        : selectedDepartureIds.filter((id) => !existingDepartureIds.includes(id)).length,
    [activeTab, selectedRateIds, existingRateIds, selectedDepartureIds, existingDepartureIds],
  );

  const handleConfirm = useCallback(async () => {
    const existingIds = activeTab === 'rate' ? existingRateIds : existingDepartureIds;
    const filters =
      activeTab === 'rate'
        ? selectedRateIds
            .filter((filterId) => !existingIds.includes(filterId))
            .map((filterId) => ({ filterId, filterType: 'RATE' as const }))
        : selectedDepartureIds
            .filter((filterId) => !existingIds.includes(filterId))
            .map((filterId) => ({ filterId, filterType: 'DEPARTURE' as const }));

    if (filters.length === 0) {
      return;
    }

    try {
      await createFilters({ inclusionId, filters });
      enqueueSnackbar(`${filters.length} filter${filters.length > 1 ? 's' : ''} added successfully`, {
        variant: 'success',
      });
      onSave();
      handleClose();
    } catch (error) {
      const errors = error?.errors;
      const errorMessage = Array.isArray(errors) && errors.length > 0 ? errors.join(', ') : 'Unknown error';
      enqueueSnackbar(`Error adding filters: ${errorMessage}`, { variant: 'error' });
    }
  }, [
    activeTab,
    selectedRateIds,
    selectedDepartureIds,
    existingRateIds,
    existingDepartureIds,
    inclusionId,
    createFilters,
    enqueueSnackbar,
    onSave,
    handleClose,
  ]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>Add Inclusion Filters</DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Rate Codes" value="rate" />
            <Tab label="Departures" value="departure" />
          </Tabs>
        </Box>

        {activeTab === 'rate' && (
          <RateCodeFilterTab
            vendorCode={vendorCode}
            selectedRateIds={selectedRateIds}
            existingRateIds={existingRateIds}
            onSelectionChange={setSelectedRateIds}
          />
        )}

        {activeTab === 'departure' && (
          <DepartureFilterTab
            vendorId={vendorId}
            vendorName={vendorName}
            selectedDepartureIds={selectedDepartureIds}
            existingDepartureIds={existingDepartureIds}
            onSelectionChange={setSelectedDepartureIds}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={newCount === 0 || isPending}
          startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {newCount > 0
            ? `Add ${newCount} ${activeTab === 'rate' ? 'rate code' : 'departure'} filter${newCount > 1 ? 's' : ''}`
            : 'Add filters'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
